import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, Flag, CheckCircle, ChatText, X,
  ArrowCounterClockwise, CaretDown, CaretUp, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api } from "./api";

/* ── helpers ────────────────────────────────────────────── */

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const TYPE_META = {
  child_signal:        { icon: Flag,             colour: "text-warning-ink",    bg: "bg-warning/15"  },
  flag_resolved:       { icon: CheckCircle,      colour: "text-teal-ink",       bg: "bg-teal/12"     },
  admin_message:       { icon: ChatText,         colour: "text-primary-strong", bg: "bg-primary/10"  },
  parent_word_query:   { icon: PaperPlaneTilt,   colour: "text-blue-ink",       bg: "bg-blue/10"     },
  word_query_resolved: { icon: CheckCircle,      colour: "text-success-ink",    bg: "bg-success/12"  },
};

const STATUS_LABEL = {
  pending_parent: { label: "Awaiting your response", variant: "warning" },
  pending_admin:  { label: "With the Bloom team",    variant: "primary" },
  escalated:      { label: "Reported to admin",      variant: "primary" },
  dismissed:      { label: "Dismissed by you",       variant: "neutral" },
  resolved:       { label: "Resolved",               variant: "success" },
};

/* ── Single notification card ────────────────────────────── */

function NotifCard({ notif, onRefresh }) {
  const [expanded,  setExpanded]  = useState(!notif.acknowledged);
  const [note,      setNote]      = useState("");
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  const meta = TYPE_META[notif.type] || TYPE_META.admin_message;
  const Icon = meta.icon;

  const respond = async (action) => {
    setSaving(true); setError("");
    try {
      await api("/notifications/respond", {
        method: "POST",
        body: { notification_id: notif.notification_id, action, parent_note: note },
      });
      onRefresh();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const acknowledge = async () => {
    try {
      await api("/notifications/acknowledge", {
        method: "POST",
        body: { notification_ids: [notif.notification_id] },
      });
      onRefresh();
    } catch { /* ignore */ }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "rounded-2xl border bg-card transition-all",
        !notif.acknowledged ? "border-primary/30 shadow-soft" : "border-border",
      )}
    >
      {/* Header row */}
      <button
        className="flex w-full items-start gap-3 p-4 text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full", meta.bg)}>
          <Icon size={15} weight="fill" className={meta.colour} />
        </span>

        <div className="min-w-0 flex-1">
          {notif.type === "child_signal" && (
            <p className="font-display text-sm font-extrabold text-foreground">
              {notif.child_name} flagged &ldquo;{notif.word}&rdquo;
            </p>
          )}
          {notif.type === "flag_resolved" && (
            <p className="font-display text-sm font-extrabold text-foreground">
              &ldquo;{notif.word}&rdquo; has been reviewed
            </p>
          )}
          {notif.type === "admin_message" && (
            <p className="font-display text-sm font-extrabold text-foreground">
              Message from Bloom team
            </p>
          )}
          {notif.type === "parent_word_query" && (
            <p className="font-display text-sm font-extrabold text-foreground">
              Word query: &ldquo;{notif.word}&rdquo;
            </p>
          )}
          {notif.type === "word_query_resolved" && (
            <p className="font-display text-sm font-extrabold text-foreground">
              Query about &ldquo;{notif.word}&rdquo; answered
            </p>
          )}
          <p className="mt-0.5 text-[11px] font-semibold text-muted-foreground">
            {timeAgo(notif.created_at)}
            {notif.status && STATUS_LABEL[notif.status] && (
              <> · <BloomBadge variant={STATUS_LABEL[notif.status].variant} className="ml-1 px-2 py-0 text-[9px]">
                {STATUS_LABEL[notif.status].label}
              </BloomBadge></>
            )}
          </p>
        </div>

        <span className="mt-1 shrink-0 text-muted-foreground">
          {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </span>
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">

              {/* Child signal detail */}
              {notif.type === "child_signal" && (
                <div className="rounded-xl bg-warning/10 px-3 py-2.5">
                  <p className="text-[10px] font-extrabold uppercase tracking-wide text-warning-ink">
                    {notif.child_name} said
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground">
                    &ldquo;{notif.child_reason}&rdquo;
                  </p>
                </div>
              )}

              {/* Flag resolved detail */}
              {notif.type === "flag_resolved" && notif.admin_note && (
                <div className="rounded-xl bg-teal/10 px-3 py-2.5">
                  <p className="text-[10px] font-extrabold uppercase tracking-wide text-teal-ink">
                    Admin note
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground">{notif.admin_note}</p>
                </div>
              )}

              {/* Admin message */}
              {notif.type === "admin_message" && (
                <p className="text-sm font-semibold text-foreground">{notif.admin_message}</p>
              )}

              {/* Parent word query — show the note they sent */}
              {notif.type === "parent_word_query" && (
                <div className="rounded-xl bg-blue/10 px-3 py-2.5">
                  <p className="text-[10px] font-extrabold uppercase tracking-wide text-blue-ink">
                    Your message
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground">
                    &ldquo;{notif.parent_note}&rdquo;
                  </p>
                </div>
              )}

              {/* Word query resolved — admin's thank-you + reply */}
              {notif.type === "word_query_resolved" && (
                <>
                  {notif.admin_message && (
                    <p className="text-sm font-semibold text-muted-foreground">{notif.admin_message}</p>
                  )}
                  {notif.admin_note && (
                    <div className="rounded-xl bg-success/10 px-3 py-2.5">
                      <p className="text-[10px] font-extrabold uppercase tracking-wide text-success-ink">
                        From the Bloom team
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-foreground">{notif.admin_note}</p>
                    </div>
                  )}
                </>
              )}

              {/* Parent's own note (if already responded) — excluded for word-query types
                  which have their own dedicated display blocks above */}
              {notif.parent_note && notif.status !== "pending_parent"
                && !["parent_word_query", "word_query_resolved"].includes(notif.type) && (
                <div className="rounded-xl bg-muted/60 px-3 py-2.5">
                  <p className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
                    Your note
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground">{notif.parent_note}</p>
                </div>
              )}

              {/* Action area for pending child signals */}
              {notif.type === "child_signal" && notif.status === "pending_parent" && (
                <div className="space-y-2.5">
                  <div>
                    <label className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
                      Your thoughts (optional)
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      placeholder="e.g. The meaning seems too complex for Year 4…"
                      className="mt-1.5 w-full rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm font-semibold text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none resize-none"
                    />
                  </div>
                  {error && <p className="text-xs font-bold text-destructive">{error}</p>}
                  <div className="flex flex-wrap gap-2">
                    <BloomButton size="sm" disabled={saving}
                      onClick={() => respond("escalate")}>
                      {saving ? "Sending…" : "Report to admin"}
                    </BloomButton>
                    <BloomButton size="sm" variant="outline" disabled={saving}
                      onClick={() => respond("dismiss")}>
                      Dismiss
                    </BloomButton>
                  </div>
                </div>
              )}

              {/* Acknowledge button for unread non-actionable notifications */}
              {!notif.acknowledged && notif.status !== "pending_parent" && (
                <button
                  onClick={acknowledge}
                  className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-foreground"
                >
                  <CheckCircle size={13} /> Mark as read
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main NotificationFeed component ─────────────────────── */

export function NotificationFeed() {
  const [notifs,  setNotifs]  = useState([]);
  const [unread,  setUnread]  = useState(0);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter,  setFilter]  = useState("all"); // all | pending | resolved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("/notifications");
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = notifs.filter((n) => {
    if (filter === "pending")  return n.status === "pending_parent";
    if (filter === "resolved") return ["resolved", "dismissed"].includes(n.status);
    return true;
  });

  const acknowledgeAll = async () => {
    const ids = notifs.filter((n) => !n.acknowledged).map((n) => n.notification_id);
    if (!ids.length) return;
    await api("/notifications/acknowledge", { method: "POST", body: { notification_ids: ids } });
    load();
  };

  return (
    <div className="relative">
      {/* Bell trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full bg-muted transition hover:bg-border"
        aria-label="Notifications"
        data-testid="notif-bell"
      >
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-primary-strong" : "text-muted-foreground"} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 font-display text-[10px] font-black text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      <AnimatePresence>
        {open && (
          <>
            <button className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 z-40 mt-2 w-[min(420px,calc(100vw-2rem))] rounded-3xl border border-border bg-card shadow-soft-lg"
            >
              {/* Panel header */}
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <p className="font-display text-sm font-black text-foreground">
                  Notifications {unread > 0 && <span className="ml-1 text-primary-strong">({unread} new)</span>}
                </p>
                <div className="flex items-center gap-2">
                  {unread > 0 && (
                    <button onClick={acknowledgeAll}
                      className="text-[11px] font-bold text-muted-foreground hover:text-foreground">
                      Mark all read
                    </button>
                  )}
                  <button onClick={load} aria-label="Refresh"
                    className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted text-muted-foreground">
                    <ArrowCounterClockwise size={13} weight="bold" />
                  </button>
                  <button onClick={() => setOpen(false)}
                    className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted text-muted-foreground">
                    <X size={13} weight="bold" />
                  </button>
                </div>
              </div>

              {/* Filter tabs */}
              <div className="flex gap-1 border-b border-border px-3 py-2">
                {[["all", "All"], ["pending", "Action needed"], ["resolved", "Resolved"]].map(([val, label]) => (
                  <button key={val} onClick={() => setFilter(val)}
                    className={cn(
                      "rounded-full px-3 py-1 text-[11px] font-extrabold transition",
                      filter === val ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted",
                    )}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Notification list */}
              <div className="max-h-[70svh] space-y-2 overflow-y-auto p-3">
                {loading && (
                  <div className="space-y-2">
                    {[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />)}
                  </div>
                )}
                {!loading && filtered.length === 0 && (
                  <div className="py-8 text-center">
                    <Bell size={32} weight="thin" className="mx-auto text-muted-foreground/40" />
                    <p className="mt-2 text-sm font-semibold text-muted-foreground">
                      {filter === "pending" ? "Nothing needs your attention" : "No notifications yet"}
                    </p>
                  </div>
                )}
                {!loading && filtered.map((n) => (
                  <NotifCard key={n.notification_id} notif={n} onRefresh={load} />
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}